import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/brand/StatusBadge";
import QuoteDialog from "@/components/quotes/QuoteDialog";
import QuoteActionsDialog from "@/components/quotes/QuoteActionsDialog";
import { useNavigate } from "react-router-dom";
import { FileText, Loader2, Plus, Pencil, Download, Send, CheckCircle2, MessageCircle, Mail, Zap, Sun, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { quoteStatusTone, eur, downloadQuotePdf } from "@/lib/crm";

export default function QuotesPanel({ customerId, onChanged }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [add, setAdd] = useState(false);
  const [edit, setEdit] = useState(null);
  const [action, setAction] = useState(null); // {quote, mode}

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/customers/${customerId}/quotes`); setRows(data); }
    catch (e) { setRows([]); }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); onChanged?.(); };
  const pdf = async (q) => { try { await downloadQuotePdf(api, q.id, q.quote_number); } catch (e) { /* ignore */ } };
  const emailQuote = async (q) => {
    try {
      const { data } = await api.post(`/quotes/${q.id}/email`, {});
      if (data.status === "sent") toast.success("Email inviata al cliente");
      else if (data.status === "not_configured") toast.warning("Email non configurata");
      else toast.error("Invio email fallito");
      refresh();
    } catch (e) { toast.error("Invio email fallito"); }
  };
  const closed = (s) => ["Accettato", "Rifiutato", "Scaduto"].includes(s);

  return (
    <div className="space-y-4" data-testid="quotes-panel">
      <div className="flex items-center justify-between">
        <h3 className="ds-section-title flex items-center gap-2"><FileText className="h-4 w-4 text-brand" /> Preventivi</h3>
        <Button size="sm" onClick={() => setAdd(true)} className="gap-1.5" data-testid="add-quote-button"><Plus className="h-4 w-4" /> Nuovo preventivo</Button>
      </div>

      {rows === null ? (
        <div className="py-10 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-brand" /></div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-slate-500">
          <FileText className="h-8 w-8 text-slate-300" />
          <div className="font-display font-semibold text-slate-700">Nessun preventivo</div>
          <p className="max-w-sm text-sm">Crea un preventivo (commodity o efficienza) per questo cliente. Il numero è automatico e il PDF scaricabile.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((q) => (
            <div key={q.id} className="ds-card flex flex-wrap items-center justify-between gap-3 p-3" data-testid={`quote-item-${q.id}`}>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs font-semibold text-slate-700">{q.quote_number}</span>
                  <span className="inline-flex items-center gap-1 text-sm text-slate-600">{q.category === "Commodity" ? <Zap className="h-3.5 w-3.5 text-warning" /> : <Sun className="h-3.5 w-3.5 text-brand" />}{q.category}</span>
                  <StatusBadge type={quoteStatusTone(q.status)}>{q.status}</StatusBadge>
                  <span className="text-sm text-slate-500">{q.product || "—"} · {eur(q.final_amount ?? q.amount)}</span>
                  {q.follow_up_overdue && <StatusBadge type="danger" icon={AlertTriangle}>Follow-up scaduto</StatusBadge>}
                </div>
                <div className="mt-1 flex flex-wrap gap-x-4 text-xs text-slate-500">
                  {q.sent_date && <span>Inviato {new Date(q.sent_date).toLocaleDateString("it-IT")}{q.sent_channel ? ` · ${q.sent_channel}` : ""}{q.days_since_sent != null ? ` · ${q.days_since_sent}g fa` : ""}</span>}
                  {q.follow_up_date && <span>Prossimo contatto: {new Date(q.follow_up_date).toLocaleDateString("it-IT")}</span>}
                  {q.lost_reason && <span className="text-danger">Motivo: {q.lost_reason}</span>}
                </div>
              </div>
              <div className="flex gap-1">
                {!closed(q.status) && <Button size="sm" variant="ghost" onClick={() => navigate(`/whatsapp?customer=${customerId}&quote=${q.id}&template=${encodeURIComponent("Invio preventivo")}`)} title="Invia preventivo via WhatsApp" data-testid={`quote-whatsapp-${q.id}`}><MessageCircle className="h-4 w-4 text-success" /></Button>}
                {!closed(q.status) && <Button size="sm" variant="ghost" onClick={() => navigate(`/whatsapp?customer=${customerId}&quote=${q.id}&template=${encodeURIComponent("Follow-up preventivo")}`)} title="Follow-up via WhatsApp" data-testid={`quote-whatsapp-followup-${q.id}`}><MessageCircle className="h-4 w-4 text-brand" /></Button>}
                {!closed(q.status) && <Button size="sm" variant="ghost" onClick={() => emailQuote(q)} title="Invia Email" data-testid={`quote-email-${q.id}`}><Mail className="h-4 w-4" /></Button>}
                {!closed(q.status) && <Button size="sm" variant="ghost" onClick={() => setAction({ quote: q, mode: "send" })} title="Segna come inviato" data-testid={`quote-send-${q.id}`}><Send className="h-4 w-4" /></Button>}
                {!closed(q.status) && <Button size="sm" variant="ghost" onClick={() => setAction({ quote: q, mode: "outcome" })} title="Registra esito" data-testid={`quote-outcome-${q.id}`}><CheckCircle2 className="h-4 w-4" /></Button>}
                <Button size="sm" variant="ghost" onClick={() => pdf(q)} title="Scarica PDF" data-testid={`quote-pdf-${q.id}`}><Download className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => setEdit(q)} title="Modifica" data-testid={`edit-quote-${q.id}`}><Pencil className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <QuoteDialog open={add} onOpenChange={setAdd} customerId={customerId} onSaved={refresh} />
      {edit && <QuoteDialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)} customerId={customerId} quote={edit} onSaved={() => { setEdit(null); refresh(); }} />}
      {action && <QuoteActionsDialog open={!!action} onOpenChange={(v) => !v && setAction(null)} quote={action.quote} mode={action.mode} onSaved={() => { setAction(null); refresh(); }} />}
    </div>
  );
}
